import { createTheme, ThemeOptions } from "@mui/material/styles";

// Palette colori EffAi
const effaiColors = {
  primary: {
    main: "#00A19C",
    dark: "#008C87",
    light: "#92D6D5",
  },
  secondary: {
    main: "#80142B",
  },
  dark: "#282828",
  light: "#E6E6E6",
  background: {
    dark: {
      level1: "#0a242e",
      level2: "#FFFFFF",
    },
    light: {
      level1: "#F2F2F2",
      level2: "#E6E6E6",
    },
  },
  // Colori condizionali per tabelle (usati per indicatori di stato)
  conditional: {
    error: "#EF5350", // Rosso
    warning: "#FF9800", // Arancione
    success: "#4CAF50", // Verde
  },
};

// Estendi il tipo Theme per includere i colori condizionali
declare module "@mui/material/styles" {
  interface Palette {
    conditional: {
      error: string;
      warning: string;
      success: string;
    };
  }
  interface PaletteOptions {
    conditional?: {
      error?: string;
      warning?: string;
      success?: string;
    };
  }
}

export const effaiTheme = createTheme({
  palette: {
    mode: "light", // Default dark mode
    primary: {
      main: effaiColors.primary.main,
      dark: effaiColors.primary.dark,
      light: effaiColors.primary.light,
      contrastText: "#fff",
    },
    secondary: {
      main: effaiColors.secondary.main,
      contrastText: "#fff",
    },
    background: {
      default: effaiColors.background.dark.level1,
      paper: effaiColors.background.dark.level2,
    },
    text: {
      primary: effaiColors.dark,
      secondary: effaiColors.primary.dark,
    },
    divider: "rgba(255, 255, 255, 0.12)",
    conditional: {
      error: effaiColors.conditional.error,
      warning: effaiColors.conditional.warning,
      success: effaiColors.conditional.success,
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          // Webkit browsers (Chrome, Safari, Edge)
          "&::-webkit-scrollbar": {
            width: "20px",
            height: "20px",
          },
          "&::-webkit-scrollbar-button": {
            display: "none",
          },
          "&::-webkit-scrollbar-track": {
            background: effaiColors.background.dark.level2,
            borderRadius: "999px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#6B6B6B",
            borderRadius: "999px",
            border: `3px solid ${effaiColors.background.dark.level2}`,
            "&:hover": {
              background: "#8B8B8B",
            },
          },
          // Firefox scrollbar
          scrollbarWidth: "auto",
          scrollbarColor: `#6B6B6B ${effaiColors.background.dark.level2}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: effaiColors.background.dark.level1,
          color: effaiColors.light,
          borderTopRightRadius: 20,
          borderBottomRightRadius: 20,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.dark.level2,
          color: effaiColors.light,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 500,
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.dark.level2,
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.dark.level2,
          borderRadius: "1rem",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          "& .MuiAccordionSummary-expandIconWrapper": {
            color: "#fff",
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          minWidth: 150,
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "& .MuiListItemIcon-root": {
            color: "rgba(80, 80, 80, 0.9)", // Grigio più scuro
            minWidth: 36,
          },
          "& .MuiListItemText-primary": {
            color: "rgba(80, 80, 80, 0.9)", // Grigio più scuro
            fontSize: "0.875rem",
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "1px solid rgba(80, 80, 80, 0.3)", // bordo esterno
          borderRadius: 8,
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(80, 80, 80, 0.3)", // linea orizzontale tra le righe
          },
          "& .MuiDataGrid-columnHeader": {
            //height: '36px',
          },
          "& .MuiDataGrid-columnSeparator": {
            color: "rgba(80, 80, 80, 0.9)", // colore dei separatori verticali tra colonne
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "1px solid rgba(80, 80, 80, 0.3)", // linea sotto l'intestazione
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid rgba(80, 80, 80, 0.3)", // linea sopra il footer
          },
          "& .MuiDataGrid-toolbar": {
            borderBottom: "1px solid rgba(80, 80, 80, 0.3)", // linea sopra il footer
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 4,
            },
          },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        root: {
          "& .MuiDialog-container, & .MuiDialog-scrollPaper": {
            alignItems: "center",
            justifyContent: "center",
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          paddingRight: 24,
          paddingBottom: 24,
        },
      },
    },
  },
} as ThemeOptions);

// Export anche come baseTheme per compatibilità
export const baseTheme = effaiTheme;
